import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, Loader2, Mail, Plus, Search, Trash2, Users } from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import DateRangeFilter, { EMPTY_RANGE, rangeActive, rangeParams } from "@/components/DateRangeFilter";
import { Pill } from "@/components/Music";
import { WriterTerms } from "@/components/Payout";
import { LoginStatus, WriterLoginBox } from "@/components/Login";
import { SignedReview, SignedTag } from "@/components/Signed";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const EMPTY = { name: "", stage_name: "", pro: "", ipi: "", email: "", phone: "", notes: "", active: true, signed: true };

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function Coverage({ w, society = "BMI" }) {
  const pct = w.credits ? Math.round((w.registered[society] / w.credits) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-[12px] text-muted2 mb-1.5">
        <span>
          {w.credits} credit{w.credits === 1 ? "" : "s"}
        </span>
        <span>
          {pct}% at {society}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface">
        <div className="h-1.5 rounded-full bg-sec-pub" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function WriterSheet({ writer, open, onOpenChange, onSaved, canEdit, canAdmin }) {
  const { settings, societyLabel } = useSettings();
  const confirm = useConfirm();
  const isNew = !writer?.writer_id;
  const [form, setForm] = useState(EMPTY);
  const [invite, setInvite] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(writer ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, writer[k] ?? EMPTY[k]])) } : EMPTY);
    setInvite({ email: writer?.email || "", password: "" });
  }, [writer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) await api.post("/writers", form);
      else await api.put(`/writers/${writer.writer_id}`, form);
      toast.success(isNew ? "Writer added" : "Saved — their credits are updated too");
      onSaved();
      if (isNew) onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!(await confirm({ title: `Remove ${writer.name}?`, body: "Only possible when they aren't credited on any track.", confirmText: "Remove", destructive: true }))) return;
    try {
      await api.delete(`/writers/${writer.writer_id}`);
      toast.success("Removed");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/writers/${writer.writer_id}/invite`, invite);
      toast.success(`Login created — give ${writer.name} the email and password. They'll choose their own on first sign-in.`);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[500px] p-0 bg-card flex flex-col gap-0">
        <div className="p-6 pb-5 border-b border-line pr-12 flex gap-4 items-center">
          <div className="w-12 h-12 rounded-full bg-brand-light text-brand flex items-center justify-center font-display font-semibold">{initials(form.name || "?")}</div>
          <div className="min-w-0">
            <SheetTitle className="font-display text-[22px] font-semibold leading-tight">{isNew ? "New writer" : writer.name}</SheetTitle>
            <SheetDescription className="text-[13px] text-muted2">{isNew ? "Add someone to the roster" : writer.stage_name || "Writer"}</SheetDescription>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {!isNew && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {settings.societies.map((s) => (
                  <div key={s.key} className="rounded-xl bg-surface px-3 py-2.5">
                    <div className="font-mono text-[11px] font-semibold">{societyLabel(s.key)}</div>
                    <div className="text-[13px] mt-0.5">
                      {writer.registered[s.key]}/{writer.credits}
                    </div>
                  </div>
                ))}
              </div>
              <Button asChild variant="outline" className="w-full">
                <Link to={`/catalog?writer=${writer.writer_id}`}>
                  View their {writer.credits} work{writer.credits === 1 ? "" : "s"} in the catalog <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </>
          )}
          <form id="writer-form" onSubmit={save} className="space-y-4">
            <fieldset disabled={!canEdit} className="space-y-4">
              <label className="block">
                <span className="block text-[12px] font-semibold mb-1.5">Legal name</span>
                <input className={inputCls} required value={form.name} onChange={set("name")} />
              </label>
              <label className="block">
                <span className="block text-[12px] font-semibold mb-1.5">Stage / company name</span>
                <input className={inputCls} value={form.stage_name} onChange={set("stage_name")} placeholder="e.g. REALYAAD ENT" />
              </label>
              <div className="grid grid-cols-[1fr_1.4fr] gap-3">
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">PRO</span>
                  <select className={inputCls} value={form.pro} onChange={set("pro")}>
                    <option value="">—</option>
                    {settings.lists.pros.map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">IPI #</span>
                  <input className={cn(inputCls, "font-mono")} value={form.ipi} onChange={set("ipi")} placeholder="Leave blank if pending" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">Email</span>
                  <input className={inputCls} type="email" value={form.email} onChange={set("email")} />
                </label>
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">Phone</span>
                  <input className={inputCls} value={form.phone} onChange={set("phone")} />
                </label>
              </div>
              {writer?.contact_email && (
                <div className="rounded-lg bg-gold-light px-3 py-2 text-[12.5px] -mt-1">
                  <b>{writer.name.split(" ")[0]} prefers to be contacted at {writer.contact_email}.</b>{" "}
                  <span className="text-muted2">Set on their profile. Their login email is unchanged.</span>
                </div>
              )}
              <label className="block">
                <span className="block text-[12px] font-semibold mb-1.5">Notes</span>
                <textarea className={cn(inputCls, "h-20 py-2")} value={form.notes} onChange={set("notes")} />
              </label>
              <div className="flex items-center justify-between rounded-xl border border-line px-3.5 py-3">
                <div>
                  <div className="text-[13.5px] font-medium">Active</div>
                  <p className="text-[12px] text-muted2">Inactive writers stay on old credits but drop down the list.</p>
                </div>
                <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} aria-label="Active" />
              </div>
              <div className={cn("flex items-center justify-between rounded-xl border px-3.5 py-3", form.signed ? "border-brand-ring bg-brand-light/40" : "border-line")}>
                <div>
                  <div className="text-[13.5px] font-medium">Signed to {settings.publisher_name || "you"}</div>
                  <p className="text-[12px] text-muted2">
                    {form.signed
                      ? "You represent them — they're paid from the royalties you collect, by their share on each song."
                      : "A co-writer signed elsewhere. They stay on your tracks for registration, but their share is paid by their own publisher."}
                    {!canAdmin && " Only an admin can change this."}
                  </p>
                </div>
                <Switch checked={!!form.signed} disabled={!canAdmin} onCheckedChange={(v) => setForm((f) => ({ ...f, signed: v }))} aria-label="Signed to you" />
              </div>
            </fieldset>
          </form>
          {!isNew && canAdmin && (
            <section className="rounded-xl border border-line p-4">
              <div className="text-[13.5px] font-semibold">Payout terms</div>
              <p className="text-[12.5px] text-muted2 mb-1">Only if this writer's deal differs from your defaults.</p>
              <WriterTerms writerId={writer.writer_id} terms={writer.payout} defaults={settings.payouts || { rate: 20, min: 25 }} canEdit onSaved={onSaved} />
            </section>
          )}
          {!isNew && canAdmin && (
            <section className="rounded-xl border border-line p-4 space-y-3">
              <div className="text-[13.5px] font-semibold">Writer login</div>
              {writer.login ? (
                <WriterLoginBox writer={writer} onChanged={onSaved} />
              ) : (
                <form onSubmit={sendInvite} className="space-y-2.5">
                  <p className="text-[12.5px] text-muted2">Give {writer.name} their own read-only login to check their works and registrations.</p>
                  <input className={inputCls} type="email" required placeholder="Their email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
                  <input className={inputCls} type="text" required minLength={8} placeholder="Temporary password (8+)" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} />
                  <Button type="submit" variant="outline" className="w-full">
                    <Mail className="w-4 h-4 mr-2" /> Create login
                  </Button>
                </form>
              )}
            </section>
          )}
        </div>
        {canEdit && (
          <div className="p-4 border-t border-line flex gap-2 justify-between">
            {!isNew ? (
              <Button variant="outline" onClick={remove} className="text-stock-out">
                <Trash2 className="w-4 h-4 mr-2" /> Remove
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" form="writer-form" disabled={busy} className="bg-brand hover:bg-brand-hover">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isNew ? "Add writer" : "Save"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Writers() {
  const { user } = useAuth();
  const canEdit = user?.role !== "writer";
  const canAdmin = ["owner", "admin"].includes(user?.role);
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useViewMode("writers", "grid");
  const [q, setQ] = useState("");
  const [range, setRange] = useState(EMPTY_RANGE);
  const [show, setShow] = useState("active");
  const [rows, setRows] = useRemembered("writers");
  const [editing, setEditing] = useState(null);
  const [reviewing, setReviewing] = useState(params.get("review") === "1");
  const [review, setReview] = useState(null);
  const req = useRef(0);
  useEffect(() => {
    if (!canAdmin) return;
    api.get("/writers/signed-review").then(({ data }) => setReview(data)).catch(() => {});
  }, [canAdmin, rows]);

  const load = useCallback(async () => {
    const id = ++req.current;
    try {
      const { data } = await api.get("/writers", { params: { q, ...rangeParams({ ...range, field: undefined }) } });
      if (id === req.current) setRows(data, !q && !rangeActive(range));
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [q, range, setRows]);

  useEffect(() => {
    const t = setTimeout(load, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const openId = params.get("open");
  useEffect(() => {
    if (openId && rows) {
      const w = rows.find((x) => x.writer_id === openId);
      if (w) setEditing(w);
    }
  }, [openId, rows]);
  useEffect(() => {
    if (editing?.writer_id && rows) {
      const fresh = rows.find((x) => x.writer_id === editing.writer_id);
      if (fresh && fresh !== editing) setEditing(fresh);
    }
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeSheet = (v) => {
    if (!v) {
      setEditing(null);
      if (openId) setParams({});
    }
  };

  const list = (rows || []).filter((w) =>
    show === "all" ? true : show === "active" ? w.active !== false : show === "no_ipi" ? !w.ipi : show === "logins" ? !!w.login : show === "signed" ? !!w.signed : show === "cowriters" ? !w.signed : true
  );

  return (
    <Layout
      title="Writers"
      subtitle={rows ? `${rows.length} on the roster · ${settings.publisher_name}` : "Loading…"}
      action={
        canEdit && (
          <div className="flex gap-2">
            {canAdmin && (
              <Button variant="outline" onClick={() => setReviewing(true)}>
                <BadgeCheck className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Signed writers</span>
              </Button>
            )}
            <Button className="bg-brand hover:bg-brand-hover" onClick={() => setEditing({})}>
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add writer</span>
            </Button>
          </div>
        )
      }
    >
      {review?.pending && (
        <section className="panel border-brand-ring px-5 py-4 mb-3 flex items-center gap-4 flex-wrap">
          <BadgeCheck className="w-5 h-5 text-brand shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold">Check which writers are signed to {settings.publisher_name || "you"}</div>
            <p className="text-[12.5px] text-muted2">
              Only signed writers are paid from your royalties now. We ticked {review.writers.filter((w) => w.signed).length} of {review.writers.length} — the ones with a login, their own tab in your sheet, or named on your MLC statements. Takes a minute to check.
            </p>
          </div>
          <Button className="bg-brand hover:bg-brand-hover" onClick={() => setReviewing(true)}>
            Check the list
          </Button>
        </section>
      )}
      <section className="panel px-5 py-4 mb-2 flex items-center gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[17px] font-semibold">{settings.publisher_name}</div>
          <div className="text-[12.5px] text-muted2">Your publishing company — the writers below are administered through it</div>
        </div>
        <div className="flex gap-7">
          <div>
            <div className="eyebrow text-[10px]">PRO</div>
            <div className="font-semibold text-sec-pro mt-1">{settings.publisher_pro || "—"}</div>
          </div>
          <div>
            <div className="eyebrow text-[10px]">Publisher IPI</div>
            <div className="font-mono mt-1">{settings.publisher_ipi || "—"}</div>
          </div>
        </div>
      </section>

      <StickyFilters>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, stage name, IPI…" aria-label="Search writers" className="h-9 w-[240px] pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
          </label>
          {[
            ["active", "Active"],
            ["all", "Everyone"],
            ["no_ipi", "Missing IPI"],
            ["logins", "Has a login"],
            ["signed", "Signed"],
            ["cowriters", "Co-writers"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setShow(k)}
              aria-pressed={show === k}
              className={cn("h-8 px-3 rounded-full text-[12.5px] font-semibold border", show === k ? "bg-brand text-white border-brand" : "bg-card border-line hover:border-brand-ring")}
            >
              {l}
            </button>
          ))}
          <div className="flex-1" />
          <DateRangeFilter value={range} onChange={setRange} />
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
        {rangeActive(range) && <p className="text-[12px] text-muted2 mt-2">Showing writers with releases in this period, counting only those releases.</p>}
      </StickyFilters>

      {!rows ? (
        <LoadingPanel rows={6} />
      ) : list.length === 0 ? (
        <div className="panel">
          <EmptyState icon={Users} title="No writers here" body={rows.length ? "Try another filter." : "Writers are added when you import your sheet, or add one yourself."} />
        </div>
      ) : mode === "grid" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {list.map((w) => (
            <article key={w.writer_id} className={cn("panel p-5 flex flex-col gap-4", w.active === false && "opacity-60")}>
              <button onClick={() => setEditing(w)} className="flex gap-3 items-center text-left">
                <div className="w-11 h-11 rounded-full bg-brand-light text-brand flex items-center justify-center font-display font-semibold shrink-0">{initials(w.name)}</div>
                <div className="min-w-0">
                  <div className="font-display text-[16.5px] font-semibold truncate hover:text-brand">
                    {w.name} <SignedTag w={w} />
                  </div>
                  <div className="text-[12px] text-muted2 truncate">{w.stage_name || "—"}</div>
                </div>
              </button>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-surface px-3 py-2">
                  <div className="eyebrow text-[9.5px]">PRO</div>
                  <div className="text-[13px] font-semibold text-sec-pro mt-0.5">{w.pro || "—"}</div>
                </div>
                <div className="rounded-lg bg-surface px-3 py-2 col-span-2 min-w-0">
                  <div className="eyebrow text-[9.5px]">IPI #</div>
                  <div className="mt-0.5">{w.ipi ? <span className="font-mono text-[12.5px]">{w.ipi}</span> : <Pill tone="warn">IPI missing</Pill>}</div>
                </div>
              </div>
              <Coverage w={w} />
              <div className="flex items-center justify-between pt-3 border-t border-line mt-auto">
                <LoginStatus login={w.login} />
                <Link to={`/catalog?writer=${w.writer_id}`} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand">
                  View works <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  {["Writer", "PRO", "IPI #", "Credits", "At BMI", "At MLC", "Problems", "Last release", "Login"].map((h) => (
                    <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((w) => (
                  <tr key={w.writer_id} className={cn("t-row cursor-pointer", w.active === false && "opacity-60")} onClick={() => setEditing(w)}>
                    <td className="t-cell">
                      <div className="font-semibold">
                        {w.name} <SignedTag w={w} />
                      </div>
                      <div className="text-[12px] text-muted2">{w.stage_name}</div>
                    </td>
                    <td className="t-cell text-sec-pro font-semibold">{w.pro || "—"}</td>
                    <td className="t-cell font-mono text-[12.5px]">{w.ipi || <Pill tone="warn">Missing</Pill>}</td>
                    <td className="t-cell font-mono">{w.credits}</td>
                    <td className="t-cell font-mono">{w.registered.BMI}</td>
                    <td className="t-cell font-mono">{w.registered.MLC}</td>
                    <td className="t-cell">{w.problems ? <Pill tone="bad">{w.problems}</Pill> : <span className="text-muted2">—</span>}</td>
                    <td className="t-cell text-[12.5px] whitespace-nowrap">{w.last_release ? formatDate(w.last_release) : "—"}</td>
                    <td className="t-cell">{w.login ? w.login.suspended ? <Pill tone="bad">Suspended</Pill> : <Pill tone="good">Yes</Pill> : <span className="text-muted2 text-[12.5px]">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <WriterSheet writer={editing} open={!!editing} onOpenChange={closeSheet} onSaved={load} canEdit={canEdit} canAdmin={canAdmin} />
      {canAdmin && (
        <SignedReview
          open={reviewing}
          onOpenChange={(v) => {
            setReviewing(v);
            if (!v && params.get("review")) setParams({});
          }}
          publisher={settings.publisher_name}
          onSaved={load}
        />
      )}
    </Layout>
  );
}
